import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import Header from "../../../Common/Layout/Header";
import {
  FiArrowLeft,
  FiBus,
  FiUser,
  FiCalendar,
  FiMapPin,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiDollarSign,
  FiPlus,
  FiMinus,
  FiEdit2,
  FiClock,
  FiUserPlus,
  FiUserMinus,
  FiInfo,
  FiCreditCard,
  FiArrowRight
} from "react-icons/fi";
import { 
  BsPersonBadge, 
  BsShieldCheck,
  BsPhone,
  BsEnvelope,
  BsIdCard
} from "react-icons/bs";
import { MdOutlineNightShelter, MdOutlineChair } from "react-icons/md";
import { TbBusStop } from "react-icons/tb";
import BusLayout from "../BusLayouts/BusLayout";
import BusBoardingPoints from "../BusBoardingPoints/BusBoardingPoints1";
import styles from "./BusSeatSelection1.module.css";

const BusSeatSelection1 = ({ mode, onNavigate, templateId, params, styleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
   const bus = params?.bus || location.state?.bus;
  const searchQuery = params?.searchQuery || location.state?.searchQuery;
   const themeVariables = {
    "--primary-color": styleConfig?.primary || "#3b82f6",
    "--secondary-color": styleConfig?.secondary || "#8b5cf6",
  };
  const [seatLayoutData, setSeatLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerCount, setPassengerCount] = useState(1);
  const [showBoardingPoints, setShowBoardingPoints] = useState(false);
  const [selectedBoardingPoint, setSelectedBoardingPoint] = useState(null);
  const [selectedDroppingPoint, setSelectedDroppingPoint] = useState(null);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [seatTypes, setSeatTypes] = useState({});
  const [fareSummary, setFareSummary] = useState({
    baseFare: 0,
    convenienceFee: 0,
    total: 0,
  });
  const [activeStep, setActiveStep] = useState(1);

  const isUpdating = useRef(false);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Format time function
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Initialize on mount
  useEffect(() => {
    if (!bus || !searchQuery) {
      if (mode === "builder") {
  onNavigate({ name: "bus-results" });
} else if (mode === "preview") {
  navigate(`/template-preview/${templateId}/bus-results`);
} else {
  navigate("/template/bus/results");
}
      return;
    }
    fetchSeatLayout();
  }, [bus, searchQuery, navigate]);

  // Initialize passenger details
  useEffect(() => {
    const initialDetails = Array.from(
      { length: passengerCount },
      (_, index) => ({
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
        seatNumber: selectedSeats[index]?.id || "",
      })
    );
    setPassengerDetails(initialDetails);
  }, [user, passengerCount]);

  // Update seat types from layout data
  useEffect(() => {
    if (seatLayoutData && seatLayoutData.seats) {
      const types = {};
      seatLayoutData.seats.forEach((seat) => {
        if (seat.available) {
          const type = seat.sleeper
            ? seat.SeatType === 1
              ? "UPPER_SLEEPER"
              : "LOWER_SLEEPER"
            : "SEATER";
          types[type] = (types[type] || 0) + 1;
        }
      });
      setSeatTypes(types);
    }
  }, [seatLayoutData]);

  // Handle seat selection changes
  useEffect(() => {
    if (isUpdating.current || selectedSeats.length === 0) return;

    isUpdating.current = true;

    // Update passenger count to match selected seats
    if (selectedSeats.length !== passengerCount) {
      setPassengerCount(selectedSeats.length);

      if (selectedSeats.length > passengerDetails.length) {
        // Add new passenger forms
        const passengersToAdd = selectedSeats.length - passengerDetails.length;
        const newPassengers = Array.from(
          { length: passengersToAdd },
          (_, index) => {
            const seatIndex = passengerDetails.length + index;
            return {
              firstName: "",
              lastName: "",
              age: "",
              title: "Mr",
              idProof: "",
              idProofNumber: "",
              email: user?.email || "",
              phone: user?.phoneNumber || "",
              seatNumber: selectedSeats[seatIndex]?.id || "",
            };
          }
        );
        setPassengerDetails((prev) => [...prev, ...newPassengers]);
      } else if (selectedSeats.length < passengerDetails.length) {
        // Remove extra passenger forms
        setPassengerDetails((prev) => prev.slice(0, selectedSeats.length));
      }
    }

    // Update seat numbers in passenger details
    const updatedDetails = passengerDetails.map((detail, index) => ({
      ...detail,
      seatNumber: selectedSeats[index]?.id || detail.seatNumber,
    }));
    setPassengerDetails(updatedDetails);

    // Update fare summary
    updateFareSummary();

    // Update active step
    if (selectedSeats.length > 0) {
      setActiveStep(2);
    }

    setTimeout(() => {
      isUpdating.current = false;
    }, 100);
  }, [selectedSeats]);

  // Fetch seat layout from API
  const fetchSeatLayout = async () => {
    setLoading(true);
    setError(null);
    try {
      const requestData = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        Source: searchQuery.fromStation,
        Destination: searchQuery.toStation,
        DateOfJourney: searchQuery.travelDate,
        InventoryType: bus.inventoryType,
        RouteScheduleId: bus.routeScheduleId,
      };
      const url = `${config.GETBUSSEATS}/${bus.supplierId}`;

      const response = await apiService.post(url, requestData);
      if (response) {
        setSeatLayoutData(response);
      } else {
        setError("Failed to load seat layout. Please try again.");
      }
    } catch (err) {
      setError("Failed to fetch seat layout. Please try again.");
      console.error("Seat layout error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle seat selection
  const handleSeatSelect = (seat) => {
    if (!seat.available || seat.bookedBy) return;

    const isAlreadySelected = selectedSeats.some((s) => s.id === seat.id);

    if (isAlreadySelected) {
      // Deselect seat
      const newSelectedSeats = selectedSeats.filter((s) => s.id !== seat.id);
      setSelectedSeats(newSelectedSeats);

      // Clear boarding points only if ALL seats are deselected
      if (newSelectedSeats.length === 0) {
        setSelectedBoardingPoint(null);
        setSelectedDroppingPoint(null);
        setActiveStep(1);
      }
    } else {
      // Select seat
      const newSelectedSeats = [...selectedSeats, seat];
      setSelectedSeats(newSelectedSeats);
    }
  };

  // Update fare summary
  const updateFareSummary = () => {
    const baseFare = selectedSeats.reduce((total, seat) => {
      return total + (parseFloat(seat.totalPriceWithTaxes) || 0);
    }, 0);

    const convenienceFee = selectedSeats.reduce((total, seat) => {
      return total + (parseFloat(seat.convenienceFee) || 100);
    }, 0);

    const total = baseFare + convenienceFee;

    setFareSummary({
      baseFare,
      convenienceFee,
      total,
    });
  };

  // Handle passenger count change
  const handlePassengerCountChange = (count) => {
    if (count < 1 || count > 6) return;

    isUpdating.current = true;

    if (count < passengerCount) {
      setPassengerCount(count);

      // If reducing passenger count, also reduce selected seats if needed
      if (selectedSeats.length > count) {
        const seatsToRemove = selectedSeats.length - count;
        setSelectedSeats((prev) => prev.slice(0, -seatsToRemove));
      }

      // Reduce passenger details
      setPassengerDetails((prev) => prev.slice(0, count));
    } else if (count > passengerCount) {
      setPassengerCount(count);

      // Add new passenger details
      const passengersToAdd = count - passengerDetails.length;
      const newPassengers = Array.from({ length: passengersToAdd }, () => ({
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
        seatNumber: "",
      }));

      setPassengerDetails((prev) => [...prev, ...newPassengers]);
    }

    setTimeout(() => {
      isUpdating.current = false;
    }, 100);
  };

  // Handle passenger detail change
  const handlePassengerDetailChange = (index, field, value) => {
    const updatedDetails = [...passengerDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };
    setPassengerDetails(updatedDetails);
  };

  // Handle email change for all passengers
  const handleEmailChange = (email) => {
    const updatedDetails = passengerDetails.map((detail) => ({
      ...detail,
      email: email,
    }));
    setPassengerDetails(updatedDetails);
  };

  // Handle phone change for all passengers
  const handlePhoneChange = (phone) => {
    const updatedDetails = passengerDetails.map((detail) => ({
      ...detail,
      phone: phone,
    }));
    setPassengerDetails(updatedDetails);
  };

  // Check if all passenger details are filled
  const areAllPassengerDetailsFilled = () => {
    return passengerDetails.every(
      (passenger) =>
        passenger.firstName &&
        passenger.lastName &&
        passenger.age &&
        passenger.idProof &&
        passenger.email &&
        passenger.phone
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `₹${Math.round(num).toLocaleString("en-IN")}`;
  };

  // Handle boarding point selection
  const handleChangeBoardingPoints = () => {
    setShowBoardingPoints(true);
  };

  const handleBoardingPointsSubmit = async (boardingPoint, droppingPoint) => {
    setSelectedBoardingPoint(boardingPoint);
    setSelectedDroppingPoint(droppingPoint);
    setShowBoardingPoints(false);
    setActiveStep(3);
  };

  const handleBoardingPointsClose = () => {
    setShowBoardingPoints(false);
  };
// REPACE YOUR handleProceedToPayment WITH THIS ONE:
const handleProceedToPayment = () => {
    // 1. Gather all data safely
    const paymentData = {
      bus,
      searchQuery,
      selectedSeats,
      passengerCount,
      passengerDetails,
      selectedBoardingPoint,
      selectedDroppingPoint,
      totalAmount: fareSummary.total,
      baseFare: fareSummary.baseFare,
      convenienceFee: fareSummary.convenienceFee,
    };

    // 2. Safely route based on the mode using HYPHENS
    if (mode === "builder") {
      onNavigate({ name: "bus-payment", params: paymentData });
    } else if (mode === "preview") {
      // FIX: Changed bus/payment to bus-payment to prevent React Router crashes
      navigate(`/template-preview/${templateId}/bus-payment`, { state: paymentData });
    } else {
      navigate("/template/bus-payment", { state: paymentData });
    }
  };
  // Clear all seats
  const handleClearAllSeats = () => {
    setSelectedSeats([]);
    setSelectedBoardingPoint(null);
    setSelectedDroppingPoint(null);
    setPassengerCount(1);
    setPassengerDetails([
      {
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
        seatNumber: "",
      },
    ]);
    setFareSummary({
      baseFare: 0,
      convenienceFee: 0,
      total: 0,
    });
    setActiveStep(1);
  };

  // Sync seats with forms
  const syncSeatsWithForms = () => {
    if (selectedSeats.length > passengerDetails.length) {
      // Add missing forms
      const formsToAdd = selectedSeats.length - passengerDetails.length;
      const newPassengers = Array.from({ length: formsToAdd }, (_, index) => {
        const seatIndex = passengerDetails.length + index;
        return {
          firstName: "",
          lastName: "",
          age: "",
          title: "Mr",
          idProof: "",
          idProofNumber: "",
          email: user?.email || "",
          phone: user?.phoneNumber || "",
          seatNumber: selectedSeats[seatIndex]?.id || "",
        };
      });
      setPassengerDetails((prev) => [...prev, ...newPassengers]);
    } else if (selectedSeats.length < passengerDetails.length) {
      // Remove extra forms
      setPassengerDetails((prev) => prev.slice(0, selectedSeats.length));
    }
    setPassengerCount(selectedSeats.length);
  };

  // Get step status
  const getStepStatus = (step) => {
    if (step === 1) {
      return selectedSeats.length > 0 ? styles.completed : styles.active;
    }
    if (step === 2) {
      if (selectedSeats.length === 0) return styles.pending;
      if (areAllPassengerDetailsFilled() && selectedSeats.length === passengerDetails.length) 
        return styles.completed;
      return styles.active;
    }
    if (step === 3) {
      if (!selectedBoardingPoint || !selectedDroppingPoint) return styles.pending;
      if (selectedBoardingPoint && selectedDroppingPoint && areAllPassengerDetailsFilled())
        return styles.completed;
      return styles.active;
    }
    return styles.pending;
  };

  // Redirect if no bus data
  if (!bus || !searchQuery) {
    return (
      <div className={styles.wrapper}>
        <Header />
        <div className={styles.errorPage}>
          <div className={styles.container}>
            <div className={styles.errorCard}>
              {/* <FiBus className={styles.errorIcon} /> */}
              <h2>Invalid Bus Selection</h2>
              <p>Please go back and try again</p>
              <button onClick={() => navigate('/template/bus/results')} className={styles.primaryButton}>
                Back to Results
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
     <div className={styles.wrapper} style={themeVariables}>
      <div className={styles.mainContent}>
        <div className={styles.container}>
          {/* Header Section */}
          <div className={styles.pageHeader}>
            <div className={styles.headerLeft}>
              <button 
                className={styles.backButton}
                onClick={() => {
  if (mode === "builder") {
    onNavigate({ name: "bus-results" });
  } else {
    navigate(-1);
  }
}}
              >
                <FiArrowLeft />
              </button>
              <div>
                <h1>Select Your Seats</h1>
                <p>Choose seats and provide passenger details</p>
              </div>
            </div>
            <div className={styles.journeyInfo}>
              <span>{searchQuery.fromStation}</span>
              <FiArrowRight />
              <span>{searchQuery.toStation}</span>
              <span className={styles.dateBadge}>
                <FiCalendar />
                {formatDate(searchQuery.travelDate)}
              </span>
            </div>
          </div>

          {/* Progress Steps */}
          <div className={styles.progressSteps}>
            <div className={`${styles.step} ${getStepStatus(1)}`}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepLabel}>Select Seats</div>
            </div>
            <div className={styles.stepConnector}></div>
            <div className={`${styles.step} ${getStepStatus(2)}`}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepLabel}>Passenger Details</div>
            </div>
            <div className={styles.stepConnector}></div>
            <div className={`${styles.step} ${getStepStatus(3)}`}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepLabel}>Boarding Points</div>
            </div>
          </div>

          <div className={styles.contentGrid}>
            {/* Main Content - Seat Layout & Passenger Forms */}
            <div className={styles.mainContentArea}>
              {/* Bus Info Card */}
              <div className={styles.busInfoCard}>
                <div className={styles.busOperator}>
                  {/* <FiBus className={styles.busIcon} /> */}
                  <div>
                    <h3>{bus.operatorName}</h3>
                    <div className={styles.busMeta}>
                      <span className={styles.busType}>{bus.busType}</span>
                      {bus.rtc && <span className={styles.rtcTag}>RTC</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.routeInfo}>
                  <div className={styles.timeInfo}>
                    <span className={styles.time}>{bus.departureTime}</span>
                    <span className={styles.location}>{searchQuery.fromStation}</span>
                  </div>
                  <div className={styles.duration}>
                    <FiClock />
                    <span>{bus.durationInMins ? Math.floor(bus.durationInMins/60) + 'h ' + (bus.durationInMins%60) + 'm' : 'N/A'}</span>
                  </div>
                  <div className={styles.timeInfo}>
                    <span className={styles.time}>{bus.arrivalTime}</span>
                    <span className={styles.location}>{searchQuery.toStation}</span>
                  </div>
                </div>
              </div>

              {/* Seat Layout Card */}
              <div className={styles.seatLayoutCard}>
                <div className={styles.cardHeader}>
                  <h3>
                    {/* <FiBus /> */}
                    Seat Layout
                  </h3>
                  <div className={styles.headerActions}>
                    <div className={styles.seatCount}>
                      <BsPersonBadge />
                      <span>{selectedSeats.length} Selected</span>
                    </div>
                    {selectedSeats.length > 0 && (
                      <button
                        className={styles.clearButton}
                        onClick={handleClearAllSeats}
                      >
                        <FiX />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.cardBody}>
                  {loading && (
                    <div className={styles.loadingContainer}>
                      <div className={styles.spinner}></div>
                      <p>Loading seat layout...</p>
                    </div>
                  )}

                  {error && (
                    <div className={styles.errorContainer}>
                      <FiAlertCircle />
                      <p>{error}</p>
                      <button onClick={fetchSeatLayout} className={styles.retryButton}>
                        Try Again
                      </button>
                    </div>
                  )}

                  {!loading && !error && seatLayoutData && (
                    <>
                      {/* Seat Legend */}
                      <div className={styles.seatLegend}>
                        <div className={styles.legendItem}>
                          <div className={`${styles.legendSeat} ${styles.available}`}></div>
                          <span>Available</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div className={`${styles.legendSeat} ${styles.selected}`}></div>
                          <span>Selected</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div className={`${styles.legendSeat} ${styles.booked}`}></div>
                          <span>Booked</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div className={`${styles.legendSeat} ${styles.ladies}`}></div>
                          <span>Ladies</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div className={`${styles.legendSeat} ${styles.sleeper}`}></div>
                          <span>Sleeper</span>
                        </div>
                      </div>

                      {/* Seat Type Info */}
                      {Object.keys(seatTypes).length > 0 && (
                        <div className={styles.seatTypeInfo}>
                          {seatTypes.SEATER && (
                            <div className={styles.seatTypeItem}>
                              <div className={`${styles.seatTypeBadge} ${styles.seater}`}>S</div>
                              <span>Seater: {seatTypes.SEATER} seats</span>
                            </div>
                          )}
                          {seatTypes.LOWER_SLEEPER && (
                            <div className={styles.seatTypeItem}>
                              <div className={`${styles.seatTypeBadge} ${styles.lowerSleeper}`}>LS</div>
                              <span>Lower Sleeper: {seatTypes.LOWER_SLEEPER} seats</span>
                            </div>
                          )}
                          {seatTypes.UPPER_SLEEPER && (
                            <div className={styles.seatTypeItem}>
                              <div className={`${styles.seatTypeBadge} ${styles.upperSleeper}`}>US</div>
                              <span>Upper Sleeper: {seatTypes.UPPER_SLEEPER} seats</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Seat Layout */}
                      <div className={styles.layoutContainer}>
                        <BusLayout
                          seatData={seatLayoutData}
                          onSeatSelect={handleSeatSelect}
                          selectedSeats={selectedSeats}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Passenger Details Forms */}
              {selectedSeats.length > 0 && (
                <div className={styles.passengerCard}>
                  <div className={styles.cardHeader}>
                    <h3>
                      <FiUser />
                      Passenger Details
                    </h3>
                    <div className={styles.passengerControls}>
                      <button
                        className={styles.controlBtn}
                        onClick={() => handlePassengerCountChange(passengerCount - 1)}
                        disabled={passengerCount <= 1}
                      >
                        <FiMinus />
                      </button>
                      <span className={styles.count}>{passengerCount}</span>
                      <button
                        className={styles.controlBtn}
                        onClick={() => handlePassengerCountChange(passengerCount + 1)}
                        disabled={passengerCount >= 6}
                      >
                        <FiPlus />
                      </button>
                    </div>
                  </div>

                  <div className={styles.cardBody}>
                    {/* Sync Warning */}
                    {selectedSeats.length !== passengerDetails.length && (
                      <div className={styles.warningAlert}>
                        <FiAlertCircle />
                        <span>Seats and forms count don't match</span>
                        <button
                          className={styles.syncButton}
                          onClick={syncSeatsWithForms}
                        >
                          Sync Forms ({selectedSeats.length})
                        </button>
                      </div>
                    )}

                    {/* Passenger Forms */}
                    <div className={styles.passengerForms}>
                      {passengerDetails.map((passenger, index) => (
                        <div key={index} className={styles.passengerForm}>
                          <div className={styles.formHeader}>
                            <div className={styles.passengerNumber}>
                              <FiUser />
                              Passenger {index + 1}
                            </div>
                            {selectedSeats[index] && (
                              <div className={styles.seatBadge}>
                                Seat: {selectedSeats[index].id}
                              </div>
                            )}
                          </div>

                          <div className={styles.formBody}>
                            <div className={styles.formRow}>
                              <div className={styles.formGroup}>
                                <label>Title *</label>
                                <select
                                  value={passenger.title}
                                  onChange={(e) =>
                                    handlePassengerDetailChange(
                                      index,
                                      "title",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="Mr">Mr</option>
                                  <option value="Ms">Ms</option>
                                  <option value="Mrs">Mrs</option>
                                  <option value="Master">Master</option>
                                </select>
                              </div>

                              <div className={styles.formGroup}>
                                <label>First Name *</label>
                                <input
                                  type="text"
                                  placeholder="First name"
                                  value={passenger.firstName}
                                  onChange={(e) =>
                                    handlePassengerDetailChange(
                                      index,
                                      "firstName",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>

                              <div className={styles.formGroup}>
                                <label>Last Name *</label>
                                <input
                                  type="text"
                                  placeholder="Last name"
                                  value={passenger.lastName}
                                  onChange={(e) =>
                                    handlePassengerDetailChange(
                                      index,
                                      "lastName",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div className={styles.formRow}>
                              <div className={styles.formGroup}>
                                <label>Age *</label>
                                <input
                                  type="number"
                                  placeholder="Age"
                                  min="1"
                                  max="100"
                                  value={passenger.age}
                                  onChange={(e) =>
                                    handlePassengerDetailChange(
                                      index,
                                      "age",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>

                              <div className={styles.formGroup}>
                                <label>ID Proof *</label>
                                <select
                                  value={passenger.idProof}
                                  onChange={(e) =>
                                    handlePassengerDetailChange(
                                      index,
                                      "idProof",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select ID Proof</option>
                                  <option value="AADHAR">Aadhar Card</option>
                                  <option value="PAN">PAN Card</option>
                                  <option value="DL">Driving License</option>
                                  <option value="VOTER">Voter ID</option>
                                  <option value="PASSPORT">Passport</option>
                                </select>
                              </div>

                              <div className={styles.formGroup}>
                                <label>ID Number</label>
                                <input
                                  type="text"
                                  placeholder="ID Number"
                                  value={passenger.idProofNumber}
                                  onChange={(e) =>
                                    handlePassengerDetailChange(
                                      index,
                                      "idProofNumber",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Contact Information */}
                    <div className={styles.contactSection}>
                      <h4>
                        <BsEnvelope />
                        Contact Information
                      </h4>
                      <div className={styles.contactGrid}>
                        <div className={styles.formGroup}>
                          <label>Email Address *</label>
                          <input
                            type="email"
                            placeholder="Enter email address"
                            value={passengerDetails[0]?.email || ""}
                            onChange={(e) => handleEmailChange(e.target.value)}
                          />
                          <span className={styles.hint}>
                            Booking confirmation will be sent here
                          </span>
                        </div>
                        <div className={styles.formGroup}>
                          <label>Phone Number *</label>
                          <input
                            type="tel"
                            placeholder="Enter phone number"
                            value={passengerDetails[0]?.phone || ""}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                          />
                          <span className={styles.hint}>
                            Important updates via SMS
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Booking Summary */}
            <div className={styles.sidebar}>
              <div className={styles.summaryCard}>
                <h3>Booking Summary</h3>

                {/* Boarding Points */}
                {selectedSeats.length > 0 && (
                  <div className={styles.summarySection}>
                    <div className={styles.sectionHeader}>
                      <FiMapPin />
                      <span>Boarding Points</span>
                    </div>

                    {selectedBoardingPoint && selectedDroppingPoint ? (
                      <div className={styles.selectedPoints}>
                        <div className={styles.point}>
                          <div className={styles.pointType}>Boarding</div>
                          <div className={styles.pointName}>
                            {selectedBoardingPoint.pointName || selectedBoardingPoint.location}
                          </div>
                          <div className={styles.pointTime}>
                            <FiClock />
                            {formatTime(selectedBoardingPoint.time)}
                          </div>
                        </div>
                        <div className={styles.pointConnector}></div>
                        <div className={styles.point}>
                          <div className={styles.pointType}>Dropping</div>
                          <div className={styles.pointName}>
                            {selectedDroppingPoint.pointName || selectedDroppingPoint.location}
                          </div>
                          <div className={styles.pointTime}>
                            <FiClock />
                            {formatTime(selectedDroppingPoint.time)}
                          </div>
                        </div>
                        <button
                          className={styles.changeButton}
                          onClick={handleChangeBoardingPoints}
                        >
                          <FiEdit2 />
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className={styles.noPoints}>
                        <p>Select boarding & dropping points</p>
                        <button
                          className={styles.selectButton}
                          onClick={() => setShowBoardingPoints(true)}
                        >
                          Select Points
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Seats */}
                <div className={styles.summarySection}>
                  <div className={styles.sectionHeader}>
                    <FiUser />
                    <span>Selected Seats ({selectedSeats.length})</span>
                  </div>

                  {selectedSeats.length > 0 ? (
                    <div className={styles.selectedSeats}>
                      {selectedSeats.map((seat, index) => {
                        const isUpper = seat.SeatType === 1;
                        const isSleeper = seat.sleeper;
                        const seatBaseFare = seat.totalPriceWithTaxes || 0;
                        const seatConvenienceFee = seat.convenienceFee || 100;
                        const seatTotal = seatBaseFare + seatConvenienceFee;
                        const passengerName = passengerDetails[index]
                          ? `${passengerDetails[index].firstName} ${passengerDetails[index].lastName}`
                          : `Passenger ${index + 1}`;

                        return (
                          <div key={seat.id} className={styles.selectedSeat}>
                            <div className={styles.seatInfo}>
                              <div className={styles.seatNumber}>{seat.id}</div>
                              <div className={styles.seatDetails}>
                                <div className={styles.seatType}>
                                  {isSleeper
                                    ? isUpper
                                      ? "Upper Sleeper"
                                      : "Lower Sleeper"
                                    : "Seater"}
                                </div>
                                <div className={styles.passengerName}>
                                  {passengerName}
                                </div>
                              </div>
                            </div>
                            <div className={styles.seatPrice}>
                              {formatCurrency(seatTotal)}
                              <button
                                className={styles.removeSeat}
                                onClick={() => handleSeatSelect(seat)}
                              >
                                <FiX />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.noSeats}>
                      <p>No seats selected</p>
                    </div>
                  )}
                </div>

                {/* Fare Breakdown */}
                {selectedSeats.length > 0 && (
                  <div className={styles.fareSection}>
                    <div className={styles.fareItem}>
                      <span>Bus Fare</span>
                      <span>{formatCurrency(fareSummary.baseFare)}</span>
                    </div>
                    <div className={styles.fareItem}>
                      <span>Convenience Fee</span>
                      <span>{formatCurrency(fareSummary.convenienceFee)}</span>
                    </div>
                    <div className={styles.fareDivider}></div>
                    <div className={`${styles.fareItem} ${styles.total}`}>
                      <span>Total Amount</span>
                      <span>{formatCurrency(fareSummary.total)}</span>
                    </div>
                  </div>
                )}

                {/* Proceed Button */}
                <div className={styles.proceedSection}>
                  {selectedSeats.length === 0 ? (
                    <button className={styles.disabledButton} disabled>
                      Select Seats to Continue
                    </button>
                  ) : selectedSeats.length !== passengerDetails.length ? (
                    <button
                      className={styles.warningButton}
                      onClick={syncSeatsWithForms}
                    >
                      Sync Seats & Forms
                    </button>
                  ) : !areAllPassengerDetailsFilled() ? (
                    <button className={styles.disabledButton} disabled>
                      Fill Passenger Details
                    </button>
                  ) : !selectedBoardingPoint || !selectedDroppingPoint ? (
                    <button
                      className={styles.primaryButton}
                      onClick={() => setShowBoardingPoints(true)}
                    >
                      Select Boarding Points
                    </button>
                  ) : (
                    <button
                      className={styles.successButton}
                      onClick={handleProceedToPayment}
                    >
                      <FiCreditCard />
                      Proceed to Payment
                    </button>
                  )}
                </div>

                {/* Security Badge */}
                <div className={styles.securityBadge}>
                  <BsShieldCheck />
                  <span>Your information is secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Boarding Points Modal */}
      {showBoardingPoints && (
        <BusBoardingPoints
          bus={bus}
          onClose={handleBoardingPointsClose}
          onSubmit={handleBoardingPointsSubmit}
          autoProceed={false}
        />
      )}
    </div>
  );
};

export default BusSeatSelection1;